import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, dt } from "@/lib/api";
import { PageHeader, StageBadge } from "@/components/tam/ui";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

function TaskCard({ t, onDone }) {
  const [note, setNote] = useState("");
  const update = async (status) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status, resolution: note });
      toast.success(`Task marked ${status}`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const c = t.contact || {};
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 tam-fade" data-testid={`task-card-${t.id}`}>
      <div className="flex items-start gap-4">
        {t.status !== "Completed" && <Checkbox className="mt-1" onCheckedChange={() => update("Completed")} data-testid={`task-checkbox-${t.id}`} />}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/accounts/${t.account_id}`} className="font-display font-semibold text-[#0B132B] hover:text-[#D9232A]">{t.account_name}</Link>
            <StageBadge stage={t.stage} /><StageBadge stage={t.status} />
          </div>
          <p className="text-xs text-slate-500 mt-1">Acc {t.acc_no} · {t.owner} · raised by {t.created_by} · {dt(t.created_at)}</p>
          <p className="text-sm mt-3 whitespace-pre-wrap">{t.summary}</p>
          {(c.contact_name || c.email || c.tel) && (
            <p className="text-xs text-slate-600 mt-2">Contact: {[c.contact_name, c.email, c.tel].filter(Boolean).join(" · ")}</p>
          )}
          {t.status !== "Completed" ? (
            <div className="mt-4 space-y-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Progress / resolution note (saved to customer notes)" data-testid={`task-note-${t.id}`} />
              <div className="flex gap-2">
                {t.status === "Open" && <Button size="sm" variant="outline" onClick={() => update("In Progress")} data-testid={`task-progress-btn-${t.id}`}>Mark in progress</Button>}
                <Button size="sm" className="bg-[#D9232A] hover:bg-[#B81920]" onClick={() => update("Completed")} data-testid={`task-complete-btn-${t.id}`}>Complete</Button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-emerald-700 mt-3">Completed by {t.completed_by} · {dt(t.completed_at)}{t.resolution ? ` — ${t.resolution}` : ""}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Tasks() {
  const [status, setStatus] = useState("Open");
  const [tasks, setTasks] = useState([]);
  const load = useCallback(() => api.get("/tasks", { params: { status } }).then((r) => setTasks(r.data)), [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="tasks-page">
      <PageHeader title="Purfleet task list" subtitle="Raised automatically when a user logs Information Sent, Qualified Lead or Quote" />
      <Tabs value={status} onValueChange={setStatus} className="mb-5">
        <TabsList>
          {["Open", "In Progress", "Completed", "All"].map((s) => <TabsTrigger key={s} value={s} data-testid={`tasks-tab-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="space-y-4">
        {tasks.length === 0 && <p className="text-slate-500 text-sm" data-testid="tasks-empty">No tasks here.</p>}
        {tasks.map((t) => <TaskCard key={t.id} t={t} onDone={load} />)}
      </div>
    </div>
  );
}
